/**
 * 面談結果のシート(「コールセンター 面談結果」の CSV)から、面談の記録をまとめて登録する(2026-10-09 発注者指示)。
 * ファイルを選ぶ → 照合の結果(取り込む・登録済み・取り込まない)を確認 → 「取り込む」。
 * 使うのは 氏名・商材・面談日・改善内容 だけ。前後の成績はシートの数字ではなく、このシステムの前後15hで比べる。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { parseCsv, readCsvFile } from '@core/lib/csv';
import { todayStr, toDateLabel } from '@core/lib/date';
import { BUSINESS_LABELS, type Operator } from '@core/lib/types';
import { formatCount } from '@core/utils/formatters';
import type { TrainingRecord } from './training';
import { parseInterviewSheet, planInterviewImport, type InterviewImportEntry } from './trainingSheet';
import { importInterviewRecords } from './trainingRepo';

const STATUS_LABEL = { add: '取り込む', dup: '取り込まない' } as const;

export default function TrainingInterviewImportModal({
  operators,
  records,
  userId,
  userName,
  onClose,
  onDone,
}: {
  operators: Operator[];
  records: TrainingRecord[];
  userId: string;
  userName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState<InterviewImportEntry[] | null>(null);
  const [trainer, setTrainer] = useState(userName);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function pick(file: File | undefined) {
    setPlan(null);
    setError('');
    if (!file) return;
    setFileName(file.name);
    try {
      const table = parseCsv(await readCsvFile(file));
      const rows = parseInterviewSheet([table.headers, ...table.rows], todayStr());
      if (rows.length === 0) throw new Error('面談の行がありません');
      setPlan(planInterviewImport(rows, operators, records));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ファイルを読めませんでした');
    }
  }

  const adds = plan?.filter((p) => p.status === 'add') ?? [];
  const errors = plan?.filter((p) => p.status === 'error').length ?? 0;
  const dups = plan?.filter((p) => p.status === 'dup').length ?? 0;

  async function run() {
    if (adds.length === 0) return;
    if (!trainer.trim()) {
      setError('担当者を入れてください');
      return;
    }
    setBusy(true);
    try {
      const n = await importInterviewRecords(adds, trainer.trim(), fileName, userId);
      toast(`面談の記録を ${formatCount(n)}件 登録しました`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? `登録できませんでした: ${e.message}` : '登録できませんでした');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(960px, 94vw)">
      <h3 className="training-modal-title">面談記録を取り込む</h3>
      <p className="ui-hint">面談結果のシートを CSV で書き出したファイルを選んでください。氏名・商材・面談日・改善内容を「面談」の記録として登録します(段階は「段階と関係ない」)。前後の成績は、このシステムの前後15hで比べます。</p>
      <div className="training-actions">
        <input type="file" accept=".csv,text/csv" aria-label="面談結果のCSV" onChange={(e) => void pick(e.target.files?.[0])} disabled={busy} />
        <label className="training-field">
          <span>担当者(シートに無いため、全件に入れる名前)</span>
          <input className="ui-input" value={trainer} onChange={(e) => setTrainer(e.target.value)} disabled={busy} />
        </label>
      </div>
      {error && <p className="training-error">{error}</p>}
      {plan && (
        <>
          <p>
            取り込む: <b>{formatCount(adds.length)}件</b>
            {dups > 0 && <> ・ 登録済み・重複: {formatCount(dups)}件</>}
            {errors > 0 && (
              <>
                {' '}
                ・ 取り込まない: <b className="training-error">{formatCount(errors)}件</b>
              </>
            )}
          </p>
          <div className="report-scroll is-auto training-preview">
            <table className="report-table no-stack">
              <caption className="sr-only">取り込む内容</caption>
              <thead>
                <tr>
                  <th scope="col">シートの氏名</th>
                  <th scope="col">マスタ</th>
                  <th scope="col">カテゴリ</th>
                  <th scope="col">面談日</th>
                  <th scope="col">改善内容</th>
                  <th scope="col">結果</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p) => (
                  <tr key={p.line} className={p.status === 'add' ? '' : 'is-muted'}>
                    <td className="is-nowrap">{p.name}</td>
                    <td className="is-nowrap">{p.opId ? `${p.opCode} ${p.operatorName}` : '—'}</td>
                    <td className="is-nowrap">{BUSINESS_LABELS[p.business]}</td>
                    <td className="is-nowrap">{toDateLabel(p.date)}</td>
                    <td className="training-import-report">{p.report || '—'}</td>
                    <td className={`is-nowrap${p.status === 'error' ? ' training-error' : ''}`}>{p.status === 'error' ? p.reason : p.status === 'dup' ? `${STATUS_LABEL.dup}(${p.reason})` : STATUS_LABEL.add}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" onClick={() => void run()} disabled={busy || adds.length === 0}>
          {busy ? '登録中…' : adds.length > 0 ? `${formatCount(adds.length)}件 取り込む` : '取り込む'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
